import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const inputStyleFor = (size) => ({
  fontSize: size === 'sm' ? 11 : 13,
  padding: size === 'sm' ? '4px 8px' : '6px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: "'Roboto Mono', monospace",
  width: 120,
});

// Single-date picker wrapper — same visual language as DateRangePicker.
// Accepts ISO string ('YYYY-MM-DD') or Date; returns the same type you gave
// (string in → string out; Date in → Date out).
// Props
//   value: string | Date | null
//   onChange(v): v is string (if value was string) else Date | null
//   minDate, maxDate: optional Date — disables dates outside
//   placeholder: optional string
//   size: 'sm' | 'md'
export function SingleDatePicker({
  value, onChange,
  minDate = null, maxDate = null,
  placeholder = 'yyyy-mm-dd', size = 'md',
  width,
}) {
  const isString = typeof value === 'string';
  const selected = value
    ? (value instanceof Date ? value : new Date(value + 'T12:00:00'))
    : null;
  const handle = (d) => {
    if (!d) return onChange(isString ? '' : null);
    if (isString) {
      const s = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      onChange(s);
    } else {
      onChange(d);
    }
  };
  const style = { ...inputStyleFor(size) };
  if (width !== undefined) style.width = width;
  return (
    <DatePicker
      selected={selected}
      onChange={handle}
      minDate={minDate}
      maxDate={maxDate}
      placeholderText={placeholder}
      dateFormat="yyyy-MM-dd"
      isClearable
      customInput={<input style={style} />}
    />
  );
}

// Reusable date-range picker built on react-datepicker + date-fns.
// Props
//   start, end: Date | null
//   onChange({ start, end }): fires after any edit
//   minDate, maxDate: optional clamp
//   placeholder: optional string for the start input
//
// Later we can swap the project's <input type="date"> instances for this.
export default function DateRangePicker({
  start, end, onChange,
  minDate = null, maxDate = null,
  startPlaceholder = 'Start date',
  endPlaceholder = 'End date',
  size = 'md', // 'sm' | 'md'
}) {
  const inputStyle = {
    fontSize: size === 'sm' ? 11 : 13,
    padding: size === 'sm' ? '4px 8px' : '6px 10px',
    border: '1px solid var(--border)',
    borderRadius: 6,
    background: 'var(--surface)',
    color: 'var(--text)',
    fontFamily: "'Roboto Mono', monospace",
    width: 120,
  };
  const box = { display: 'flex', alignItems: 'center', gap: 6 };
  const label = { fontSize: 10, color: 'var(--dim)' };
  return (
    <div style={box}>
      <DatePicker
        selected={start}
        onChange={(d) => onChange({ start: d, end: end && d && d > end ? d : end })}
        selectsStart
        startDate={start}
        endDate={end}
        minDate={minDate}
        maxDate={maxDate}
        placeholderText={startPlaceholder}
        dateFormat="yyyy-MM-dd"
        isClearable
        customInput={<input style={inputStyle} />}
      />
      <span style={label}>→</span>
      <DatePicker
        selected={end}
        onChange={(d) => onChange({ start, end: d })}
        selectsEnd
        startDate={start}
        endDate={end}
        minDate={start || minDate}
        maxDate={maxDate}
        placeholderText={endPlaceholder}
        dateFormat="yyyy-MM-dd"
        isClearable
        customInput={<input style={inputStyle} />}
      />
    </div>
  );
}
